import { useEffect, useState } from "react";
import { Bell, Megaphone } from "lucide-react";
import { PageHeader, EmptyState } from "../components/Primitives";
import { Modal } from "../components/Modal";
import { api, errorText, formatDate } from "../lib/api";

export default function Notifications({ user }) {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({ title: "", message: "", type: "INFO", priority: "NORMAL", target: "ALL", library_ids: [], send_email: false });
  const [libraries, setLibraries] = useState([]);
  const [error, setError] = useState("");

  const load = async () => {
    try {
      const res = await api.get("/notifications");
      setItems(res.data.data);
      setUnread(res.data.unread);
      if (user.role === "MAIN_ADMIN") {
        const libRes = await api.get("/libraries");
        setLibraries(libRes.data.data);
      }
    } catch (err) {
      setError(errorText(err));
    }
  };

  useEffect(() => {
    load();
  }, [user.role]);

  const markAll = async () => {
    try {
      await api.post("/notifications/read-all");
      load();
    } catch (err) {
      setError(errorText(err));
    }
  };

  const broadcast = async (event) => {
    event.preventDefault();
    try {
      await api.post("/notifications", form);
      setShow(false);
      setForm({ title: "", message: "", type: "INFO", priority: "NORMAL", target: "ALL", library_ids: [], send_email: false });
      load();
    } catch (err) {
      setError(errorText(err));
    }
  };

  return (
    <div className="page-enter">
      <PageHeader
        eyebrow="MESSAGES"
        title="Notifications"
        description={`${unread} unread · in-app + email delivery when enabled.`}
        action={
          <div style={{ display: "flex", gap: 10 }}>
            <button className="button button-outline" onClick={markAll} data-testid="mark-all-read-button">Mark all read</button>
            {user.role === "MAIN_ADMIN" ? (
              <button className="button button-primary" onClick={() => setShow(true)} data-testid="broadcast-button">
                <Megaphone size={16} /> Broadcast
              </button>
            ) : null}
          </div>
        }
      />
      {error ? <div className="error-message">{error}</div> : null}

      <section className="content-card">
        {items.length ? (
          <ul className="notification-list" data-testid="notifications-list">
            {items.map((item) => (
              <li key={item.id} className="notification-item" data-testid={`notification-${item.id}`}>
                <div className="notification-icon"><Bell size={18} /></div>
                <div>
                  <strong>{item.title}</strong>
                  <p>{item.message}</p>
                  <small>{item.library_name || item.audience || "Platform"} · {formatDate(item.created_at)}</small>
                </div>
                <span className={`notification-type ${item.type?.toLowerCase() || "info"}`}>{item.type || "INFO"}</span>
              </li>
            ))}
          </ul>
        ) : (
          <EmptyState icon={<Bell size={22} />} title="You're all caught up" hint="New notifications will appear here as events unfold." />
        )}
      </section>

      {show ? (
        <Modal title="Broadcast notification" eyebrow="PLATFORM MESSAGE" close={() => setShow(false)} testId="broadcast-modal" wide>
          <form className="modal-form grid-form" onSubmit={broadcast}>
            <label className="grid-span-2">Title<input required value={form.title} onChange={(event) => setForm({ ...form, title: event.target.value })} data-testid="broadcast-title-input" /></label>
            <label className="grid-span-2">Message<textarea required rows={4} value={form.message} onChange={(event) => setForm({ ...form, message: event.target.value })} data-testid="broadcast-message-input" /></label>
            <label>Type
              <select value={form.type} onChange={(event) => setForm({ ...form, type: event.target.value })}>
                <option value="INFO">Info</option>
                <option value="WARNING">Warning</option>
                <option value="SUCCESS">Success</option>
                <option value="PAYMENT">Payment</option>
                <option value="SUBSCRIPTION">Subscription</option>
                <option value="SYSTEM">System</option>
              </select>
            </label>
            <label>Priority
              <select value={form.priority} onChange={(event) => setForm({ ...form, priority: event.target.value })}>
                <option value="LOW">Low</option>
                <option value="NORMAL">Normal</option>
                <option value="HIGH">High</option>
              </select>
            </label>
            <label>Target
              <select value={form.target} onChange={(event) => setForm({ ...form, target: event.target.value })} data-testid="broadcast-target-select">
                <option value="ALL">All libraries</option>
                <option value="ACTIVE">Active libraries</option>
                <option value="EXPIRING">Expiring soon</option>
                <option value="EXPIRED">Expired libraries</option>
                <option value="SELECTED">Selected libraries</option>
              </select>
            </label>
            <label>
              <input type="checkbox" checked={form.send_email} onChange={(event) => setForm({ ...form, send_email: event.target.checked })} /> Also send via email
            </label>
            {form.target === "SELECTED" ? (
              <label className="grid-span-2">Pick libraries
                <select multiple value={form.library_ids} onChange={(event) => setForm({ ...form, library_ids: Array.from(event.target.selectedOptions).map((option) => option.value) })} data-testid="broadcast-library-select">
                  {libraries.map((library) => (
                    <option key={library.id} value={library.id}>{library.name}</option>
                  ))}
                </select>
              </label>
            ) : null}
            <div className="modal-actions grid-span-2">
              <button type="button" className="button button-outline" onClick={() => setShow(false)}>Cancel</button>
              <button className="button button-primary" type="submit" data-testid="send-broadcast-button">Send</button>
            </div>
          </form>
        </Modal>
      ) : null}
    </div>
  );
}
